import { Injectable } from '@angular/core';
import type {
  Conference,
  Connection,
  ConnectionOptions,
  ConnectionStats,
  CreateCameraTrackOptions,
  CreateMicTrackOptions,
  JitsiApi,
  LocalTrack,
  RemoteTrack,
} from './jitsi-api';
import type { ConferenceOptions, JitsiInitOptions } from './jitsi-config';
import { readJitsiDiagnostics } from './jitsi-config';
import { JitsiConnectionError, JitsiTrackCreationError } from './jitsi-errors';
import {
  LocalTrackAdapter,
  RemoteTrackAdapter,
  unwrapTrack,
} from './track-adapters';
import type {
  JitsiConferenceLike,
  JitsiConnectionLike,
  JitsiLocalTrackLike,
  JitsiMeetJSStatic,
  JitsiRemoteTrackLike,
} from '../../../types/lib-jitsi-meet';

/**
 * The only file in the application that knows `JitsiMeetJS` exists (D21).
 *
 * Everything crossing this boundary is narrowed to the interfaces in
 * `jitsi-api.ts`. In particular the library's `createLocalTracks` resolves to
 * `JitsiLocalTrack[] | JitsiTrackError` — a union that is a trap for callers, so
 * the adapter checks it once here and throws a typed error rather than letting
 * an error object flow onward disguised as a track.
 */

const CONNECTION_TIMEOUT_MS = 15_000;

function requireGlobal(): JitsiMeetJSStatic {
  const api = window.JitsiMeetJS;
  if (!api) {
    throw new JitsiConnectionError(
      'JitsiMeetJS is not loaded — check that lib-jitsi-meet.min.js is included in index.html',
    );
  }
  return api;
}

@Injectable()
export class JitsiAdapter implements JitsiApi {
  private jitsi: JitsiMeetJSStatic | null = null;

  async init(options: JitsiInitOptions): Promise<void> {
    const api = requireGlobal();
    api.setLogLevel(api.logLevels[readJitsiDiagnostics().logLevel]);
    api.init(options);
    this.jitsi = api;
  }

  get events(): { conference: Record<string, string>; connection: Record<string, string> } {
    const api = this.jitsi ?? requireGlobal();
    return { conference: api.events.conference, connection: api.events.connection };
  }

  createConnection(options: ConnectionOptions): Connection {
    const api = this.jitsi ?? requireGlobal();

    const raw = new api.JitsiConnection(null, null, {
      hosts: {
        domain: options.domain,
        muc: `conference.${options.domain}`,
        focus: `focus.${options.domain}`,
      },
      serviceUrl: options.serviceUrl,
      clientNode: 'http://jitsi.org/jitsimeet',
      // Use the videobridge's colibri websocket (wss on 443) for the bridge
      // channel, as the deployment's own Jitsi Meet does. This lib-jitsi-meet
      // defaults to SCTP over the media path when the bridge offers both, and
      // over the bridge that channel did not open from the Android WebView:
      // audio flowed, but with no receiver constraints reaching the bridge no
      // video was sent in either direction. Peer-to-peer calls never use it,
      // which is why browser-to-browser calls were unaffected.
      bridgeChannel: { preferSctp: false },
    });

    return new JitsiConnectionAdapter(raw, api);
  }

  async createMicTrack(options: CreateMicTrackOptions): Promise<LocalTrack> {
    const api = this.jitsi ?? requireGlobal();

    // `constraints` is absent from the shipped type definitions but is read at
    // runtime — verified in the bundle, where `getConstraints()` honours a
    // caller-supplied non-boolean `constraints.audio` and otherwise synthesises
    // one from the engine-global flags. Passing it is what keeps the microphone's
    // DSP independent of the engine settings the stethoscope needs (D16).
    const tracks = await api.createLocalTracks({
      devices: ['audio'],
      micDeviceId: options.micDeviceId,
      constraints: { audio: options.constraints },
    } as Parameters<JitsiMeetJSStatic['createLocalTracks']>[0]);

    if (!Array.isArray(tracks) || tracks.length === 0) {
      throw new JitsiTrackCreationError(
        'createLocalTracks did not return a track — the device may be in use or the permission denied',
      );
    }

    const audio = tracks.find((t) => t.getType() === 'audio');
    if (!audio) {
      throw new JitsiTrackCreationError(
        'createLocalTracks returned no audio track',
      );
    }

    return new LocalTrackAdapter(audio);
  }

  async createCameraTrack(
    options: CreateCameraTrackOptions = {},
  ): Promise<LocalTrack> {
    const api = this.jitsi ?? requireGlobal();

    const tracks = await api.createLocalTracks({
      devices: ['video'],
      ...(options.cameraDeviceId
        ? { cameraDeviceId: options.cameraDeviceId }
        : {}),
      resolution: options.resolution ?? '720',
    });

    if (!Array.isArray(tracks) || tracks.length === 0) {
      throw new JitsiTrackCreationError(
        'createLocalTracks returned no camera track — the camera may be in use or the permission denied',
      );
    }

    const video = tracks.find((t) => t.getType() === 'video');
    if (!video) {
      throw new JitsiTrackCreationError(
        'createLocalTracks returned no video track',
      );
    }

    return new LocalTrackAdapter(video);
  }

  createTrackFromStream(stream: MediaStream, sourceType: string): LocalTrack {
    const api = this.jitsi ?? requireGlobal();

    const [mediaTrack] = stream.getAudioTracks();
    if (!mediaTrack) {
      throw new JitsiTrackCreationError(
        'stream has no audio track to wrap',
      );
    }

    const tracks = api.createLocalTracksFromMediaStreams([
      { mediaType: 'audio', sourceType, stream, track: mediaTrack },
    ]);

    const track = tracks[0];
    if (!track) {
      throw new JitsiTrackCreationError(
        'createLocalTracksFromMediaStreams returned nothing',
      );
    }

    return new LocalTrackAdapter(track);
  }

  enumerateDevices(): Promise<MediaDeviceInfo[]> {
    const api = this.jitsi ?? requireGlobal();
    return new Promise((resolve) => {
      api.mediaDevices.enumerateDevices((devices) => resolve(devices));
    });
  }
}

class JitsiConnectionAdapter implements Connection {
  /** Set by `disconnect()` so a deliberate hang-up is not read as a failure. */
  private intentionalDisconnect = false;

  constructor(
    private readonly connection: JitsiConnectionLike,
    private readonly api: JitsiMeetJSStatic,
  ) {}

  connect(): Promise<void> {
    const events = this.api.events.connection;

    return new Promise<void>((resolve, reject) => {
      const cleanup = (): void => {
        window.clearTimeout(timer);
        this.connection.removeEventListener(events['CONNECTION_ESTABLISHED'], onOk);
        this.connection.removeEventListener(events['CONNECTION_FAILED'], onFail);
      };

      const onOk = (): void => {
        cleanup();
        resolve();
      };

      const onFail = (): void => {
        cleanup();
        reject(new JitsiConnectionError('connection failed'));
      };

      const timer = window.setTimeout(() => {
        cleanup();
        reject(
          new JitsiConnectionError(
            `connection timed out after ${CONNECTION_TIMEOUT_MS}ms`,
          ),
        );
      }, CONNECTION_TIMEOUT_MS);

      this.connection.addEventListener(events['CONNECTION_ESTABLISHED'], onOk);
      this.connection.addEventListener(events['CONNECTION_FAILED'], onFail);
      this.connection.connect();
    });
  }

  async disconnect(): Promise<void> {
    this.intentionalDisconnect = true;
    await this.connection.disconnect();
  }

  onDisconnected(handler: (reason: string) => void): () => void {
    const event = this.api.events.connection['CONNECTION_DISCONNECTED'];
    const wrapped = (reason: unknown): void => {
      // Our own `disconnect()` raises this too. Reporting a deliberate hang-up
      // as a failure would start a reconnect loop against a call the user just
      // ended.
      if (this.intentionalDisconnect) return;
      handler(String(reason ?? 'connection disconnected'));
    };

    this.connection.addEventListener(event, wrapped);
    return () => this.connection.removeEventListener(event, wrapped);
  }

  initConference(roomName: string, options: ConferenceOptions): Conference {
    // Room names are case-sensitive on the bridge; lowercasing avoids two
    // participants silently landing in different rooms.
    const raw = this.connection.initJitsiConference(
      roomName.toLowerCase(),
      options,
    );
    return new ConferenceAdapter(
      raw,
      this.api.events.conference,
      this.api.events.connectionQuality ?? {},
    );
  }
}

class ConferenceAdapter implements Conference {
  constructor(
    private readonly conference: JitsiConferenceLike,
    private readonly events: Record<string, string>,
    private readonly qualityEvents: Record<string, string>,
  ) {}

  join(): void {
    this.conference.join();
  }

  leave(): Promise<void> {
    return this.conference.leave();
  }

  addTrack(track: LocalTrack): Promise<void> {
    return this.conference.addTrack(unwrapTrack(track));
  }

  replaceTrack(
    oldTrack: LocalTrack | null,
    newTrack: LocalTrack | null,
  ): Promise<void> {
    return this.conference.replaceTrack(
      oldTrack ? unwrapTrack(oldTrack) : null,
      newTrack ? unwrapTrack(newTrack) : null,
    );
  }

  broadcastEndpointMessage(payload: object): void {
    // `sendMessage(payload, to, sendThroughVideobridge)` is the supported
    // successor to the deprecated `sendEndpointMessage`. All three arguments
    // matter: an empty recipient broadcasts, and the third must be true or the
    // library takes the other branch and sends the object as an XMPP *chat*
    // message rather than over the datachannel — verified in the shipped
    // bundle, where only the videobridge branch reaches sendEndpointMessage.
    this.conference.sendMessage(payload, '', true);
  }

  sendTextMessage(text: string): void {
    this.conference.sendTextMessage(text);
  }

  setDisplayName(name: string): void {
    this.conference.setDisplayName(name);
  }

  setLocalParticipantProperty(name: string, value: string): void {
    this.conference.setLocalParticipantProperty(name, value);
  }

  requestRemoteVideo(maxHeight: number): void {
    this.conference.setReceiverConstraints({
      // Every remote video, at the library's own default bandwidth estimate.
      lastN: -1,
      assumedBandwidthBps: -1,
      defaultConstraints: { maxHeight },
    });
  }

  /** Total for the same reason as `displayNameOf`: callers run in presence handlers. */
  getParticipantProperty(id: string, name: string): string | null {
    try {
      const value = this.conference.getParticipantById(id)?.getProperty(name);
      return typeof value === 'string' && value !== '' ? value : null;
    } catch {
      return null;
    }
  }

  getRemoteParticipantIds(): string[] {
    try {
      return this.conference.getParticipants().map((p) => p.getId());
    } catch {
      return [];
    }
  }

  on(event: string, handler: (...args: never[]) => void): void {
    this.conference.on(event, handler);
  }

  off(event: string, handler: (...args: never[]) => void): void {
    this.conference.off(event, handler);
  }

  onRemoteTrackAdded(handler: (track: RemoteTrack) => void): () => void {
    return this.subscribeTrack('TRACK_ADDED', handler);
  }

  onRemoteTrackRemoved(handler: (track: RemoteTrack) => void): () => void {
    return this.subscribeTrack('TRACK_REMOVED', handler);
  }

  onTrackMuteChanged(handler: (track: RemoteTrack) => void): () => void {
    return this.subscribeTrack('TRACK_MUTE_CHANGED', handler);
  }

  /**
   * DOMINANT_SPEAKER_CHANGED, if this build of lib-jitsi-meet has it.
   *
   * `subscribe` already no-ops on an unknown event name, so an older library
   * simply never reports a speaker and the indicator stays off — it does not
   * throw and does not take the conference down with it.
   */
  onDominantSpeakerChanged(
    handler: (id: string | null) => void,
  ): () => void {
    return this.subscribe('DOMINANT_SPEAKER_CHANGED', (id: string | null) => {
      handler(id ?? null);
    });
  }

  onUserJoined(
    handler: (id: string, displayName: string) => void,
  ): () => void {
    return this.subscribe('USER_JOINED', (id: string) => {
      handler(id, this.displayNameOf(id));
    });
  }

  /**
   * A remote participant's display name, falling back to their id.
   *
   * Deliberately total: this runs inside an XMPP presence handler, and an
   * exception here does not merely lose the name — Strophe removes *all* its
   * handlers on an uncaught error, so the two sides stop seeing each other
   * entirely. A missing name is also perfectly ordinary, since USER_JOINED can
   * arrive before presence carries one.
   */
  private displayNameOf(id: string): string {
    try {
      const name = this.conference.getParticipantById(id)?.getDisplayName();
      return name?.trim() || id;
    } catch {
      return id;
    }
  }

  onUserLeft(handler: (id: string) => void): () => void {
    return this.subscribe('USER_LEFT', (id: string) => handler(id));
  }

  onConferenceFailed(handler: (error: string) => void): () => void {
    return this.subscribe('CONFERENCE_FAILED', (error: unknown) =>
      handler(String(error)),
    );
  }

  onConnectionInterrupted(handler: () => void): () => void {
    return this.subscribe('CONNECTION_INTERRUPTED', () => handler());
  }

  onConnectionRestored(handler: () => void): () => void {
    return this.subscribe('CONNECTION_RESTORED', () => handler());
  }

  onBridgeChannel(handler: (open: boolean) => void): () => void {
    const offOpened = this.subscribe('DATA_CHANNEL_OPENED', () => handler(true));
    const offClosed = this.subscribe('DATA_CHANNEL_CLOSED', () => handler(false));
    return () => {
      offOpened();
      offClosed();
    };
  }

  onConferenceJoined(handler: () => void): () => void {
    return this.subscribe('CONFERENCE_JOINED', () => handler());
  }

  onKicked(handler: (reason: string) => void): () => void {
    // (actor, reason, isReplaced). The library has already called leave().
    return this.subscribe('KICKED', (_actor: unknown, reason: unknown) =>
      handler(typeof reason === 'string' && reason ? reason : 'kicked'),
    );
  }

  onDisplayNameChanged(
    handler: (id: string, displayName: string) => void,
  ): () => void {
    return this.subscribe('DISPLAY_NAME_CHANGED', (id: string, name: unknown) => {
      const trimmed = typeof name === 'string' ? name.trim() : '';
      if (trimmed) handler(id, trimmed);
    });
  }

  onParticipantPropertyChanged(
    handler: (id: string, name: string, value: string | null) => void,
  ): () => void {
    // (participant, name, oldValue, newValue).
    return this.subscribe(
      'PARTICIPANT_PROPERTY_CHANGED',
      (
        participant: { getId?: () => string },
        name: string,
        _old: unknown,
        value: unknown,
      ) => {
        const id = participant?.getId?.();
        if (!id) return;
        handler(id, name, typeof value === 'string' && value !== '' ? value : null);
      },
    );
  }

  onLocalStats(handler: (stats: ConnectionStats) => void): () => void {
    return this.subscribeQuality('LOCAL_STATS_UPDATED', (raw: unknown) =>
      handler(toConnectionStats(raw)),
    );
  }

  onRemoteStats(
    handler: (id: string, stats: ConnectionStats) => void,
  ): () => void {
    return this.subscribeQuality('REMOTE_STATS_UPDATED', (id: unknown, raw: unknown) => {
      if (typeof id === 'string' && id) handler(id, toConnectionStats(raw));
    });
  }

  /**
   * Connection-quality events are diagnostics, so unlike `subscribe` a missing
   * constant is not an error: a build without the module reports no stats and
   * the call carries on. Handlers are guarded for the same reason — a bad stats
   * payload must never throw inside the library.
   */
  private subscribeQuality(
    eventName: string,
    handler: (...args: never[]) => void,
  ): () => void {
    const event = this.qualityEvents[eventName];
    if (!event) return () => undefined;

    const guarded = (...args: never[]): void => {
      try {
        handler(...args);
      } catch {
        // Diagnostics only; nothing to recover.
      }
    };
    this.conference.on(event, guarded);
    return () => this.conference.off(event, guarded);
  }

  onEndpointMessage(
    handler: (participantId: string, payload: object) => void,
  ): () => void {
    return this.subscribe(
      'ENDPOINT_MESSAGE_RECEIVED',
      (participant: { getId?: () => string }, payload: object) => {
        handler(participant?.getId?.() ?? 'unknown', payload);
      },
    );
  }

  /**
   * Subscribe to a track event, skipping our own tracks.
   *
   * lib-jitsi-meet reports local tracks through TRACK_ADDED too. Rendering one
   * would put the participant's own microphone into their own speaker — an echo
   * loop, and on the patient side that speaker is a TV in the same room as the
   * microphone.
   */
  private subscribeTrack(
    eventName: string,
    handler: (track: RemoteTrack) => void,
  ): () => void {
    return this.subscribe(eventName, (track: JitsiRemoteTrackLike) => {
      if (track?.isLocal?.()) return;
      handler(new RemoteTrackAdapter(track));
    });
  }

  private subscribe(
    eventName: string,
    handler: (...args: never[]) => void,
  ): () => void {
    const event = this.events[eventName];
    if (!event) {
      // A renamed constant would otherwise fail silently: `on(undefined, fn)`
      // never throws and never fires.
      throw new JitsiConnectionError(
        `unknown conference event "${eventName}"`,
      );
    }

    this.conference.on(event, handler);
    return () => this.conference.off(event, handler);
  }
}


/**
 * Normalise lib-jitsi-meet's stats object.
 *
 * Shape verified in the v2180 bundle (`ConnectionQuality._localStats`):
 * `{ connectionQuality, jvbRTT, packetLoss: {download, upload},
 *    bitrate: {download, upload, audio: {…}, video: {…}},
 *    transport: [{ type, localCandidateType, remoteCandidateType, networkType, p2p }] }`.
 * Remote stats carry only quality, rtt, loss and total bitrate. Anything absent
 * or non-numeric becomes null rather than 0, because 0 would read as "nothing
 * is arriving".
 */
function toConnectionStats(raw: unknown): ConnectionStats {
  const r = (raw ?? {}) as Record<string, unknown>;
  const loss = asRecord(r['packetLoss']);
  const bitrate = asRecord(r['bitrate']);
  const audio = asRecord(bitrate['audio']);
  const video = asRecord(bitrate['video']);
  const transports = Array.isArray(r['transport']) ? r['transport'] : [];
  const first = transports.length > 0 ? asRecord(transports[0]) : null;

  return {
    quality: num(r['connectionQuality']),
    rttMs: num(r['jvbRTT']),
    packetLossPct: { download: num(loss['download']), upload: num(loss['upload']) },
    bitrateKbps: {
      download: num(bitrate['download']),
      upload: num(bitrate['upload']),
      audioDownload: num(audio['download']),
      audioUpload: num(audio['upload']),
      videoDownload: num(video['download']),
      videoUpload: num(video['upload']),
    },
    transport: first
      ? {
          protocol: String(first['type'] ?? ''),
          localCandidate: String(first['localCandidateType'] ?? ''),
          remoteCandidate: String(first['remoteCandidateType'] ?? ''),
          networkType: String(first['networkType'] ?? ''),
          p2p: Boolean(first['p2p']),
        }
      : null,
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
